import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, Send } from "lucide-react";

const AI_ICON = "https://static.prod-images.emergentagent.com/jobs/c1134d5f-8ffa-4e52-be31-05765f8e9179/images/3fa0d1f87b9d79b05d6732e52c6dcf9e92c88a874af77e436fefa6f9ba640d5e.png";

const STARTERS = [
  "Explain my latest blood sugar reading",
  "Give me a weekly health summary",
  "Suggest 3 lifestyle improvements",
  "Should I be concerned about my BP?",
];

export default function AIChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    setError("");
    setInput("");
    setMessages(m => [...m, { role: "user", content: msg, id: Date.now() }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply, id: Date.now() + 1 }]);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] md:h-screen max-w-3xl mx-auto" data-testid="ai-page">
      <div className="px-5 pt-5 pb-3 border-b border-slate-200 bg-white flex items-center gap-3">
        <img src={AI_ICON} alt="" className="w-11 h-11 rounded-2xl" />
        <div>
          <h1 className="font-heading font-bold text-slate-900 text-lg leading-tight">Health Assistant</h1>
          <p className="text-xs text-slate-500">Powered by Claude Sonnet 4.5</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="max-w-md mx-auto text-center mt-8" data-testid="ai-empty">
            <img src={AI_ICON} alt="" className="w-20 h-20 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-1">How can I help today?</h2>
            <p className="text-sm text-slate-500 mb-6">Ask about your readings, trends, or lifestyle improvements.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STARTERS.map((s, i) => (
                <button key={i} onClick={() => send(s)} data-testid={`ai-starter-${i}`} className="text-left text-sm bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm rounded-xl px-4 py-3 text-slate-700 transition">
                  <Sparkles className="w-3.5 h-3.5 text-blue-500 inline mr-1.5" />{s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-blue-600 text-white rounded-br-md"
                : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"
            }`}>
              {m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start" data-testid="ai-typing">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 text-sm text-slate-500 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 max-w-md mx-auto">{error}</div>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-200 bg-white px-4 py-3 flex items-center gap-2" data-testid="ai-form">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything about your health…" className="h-11 rounded-full bg-slate-50 border-slate-200 px-4" data-testid="ai-input" />
        <Button type="submit" disabled={loading || !input.trim()} className="h-11 w-11 p-0 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="ai-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
